'use client';

import Button from '@/components/Button';
import Image from '@/components/Image';
import { useTranslations } from '@/i18n/use-translations';
import type { ReactNode } from 'react';

import {
  getErrorPageDisplayModel,
  type ErrorPageAction,
  type ErrorPageDisplayType,
} from './errorPage';

interface ErrorPageViewProps {
  type: ErrorPageDisplayType;
  recommendations?: ReactNode;
  onTryAgain?: () => void;
  onBackHome: () => void;
}

export default function ErrorPageView({
  type,
  recommendations,
  onTryAgain,
  onBackHome,
}: ErrorPageViewProps) {
  const errorT = useTranslations('error');
  const notFoundT = useTranslations('notFound');
  const model = getErrorPageDisplayModel(type);
  const isNotFound = type === '404';
  const showRecommendations = model.showRecommendations && Boolean(recommendations);
  const description = isNotFound ? notFoundT('description') : errorT('description');

  const handleAction = (action: ErrorPageAction) => {
    if (action === 'retry') {
      onTryAgain?.();
      return;
    }
    onBackHome();
  };

  return (
    <main className="min-h-screen bg-[#f2f3f5] font-regular text-[#282828]">
      <section
        className={`flex items-center justify-center px-24 text-center ${
          showRecommendations
            ? 'min-h-[348px] lg:min-h-[538px]'
            : 'min-h-[calc(100vh-48px)] lg:min-h-[calc(100vh-104px)]'
        }`}
        aria-labelledby="error-page-title"
      >
        <div
          className={`flex w-full flex-col items-center ${isNotFound ? 'max-w-[654px]' : 'max-w-[416px]'}`}
        >
          <Image
            src={model.illustration}
            alt=""
            width={256}
            height={256}
            className="size-[180px] lg:size-[256px]"
            priority
          />
          {model.code ? (
            <h1
              id="error-page-title"
              className="font-bb text-[56px] leading-[56px] lg:text-[64px] lg:leading-[64px]"
            >
              {model.code}
            </h1>
          ) : (
            <h1 id="error-page-title" className="sr-only">
              {errorT('title')}
            </h1>
          )}
          <p
            className={`text-[12px] leading-[14px] lg:text-[16px] lg:leading-[19px] ${
              isNotFound ? 'mt-8' : 'mt-24'
            }`}
          >
            {description}
          </p>
          <div className="mt-[32px] flex w-full justify-center gap-[12px] lg:gap-[16px]">
            {model.actions.map((action) => {
              const isRetry = action === 'retry';
              return (
                <Button
                  key={action}
                  variant={isRetry ? 'outlined' : 'contained'}
                  color="primary"
                  onClick={() => handleAction(action)}
                  sx={{
                    width: isNotFound
                      ? { xs: '100%', lg: '320px' }
                      : { xs: 'calc((100% - 12px) / 2)', lg: '200px' },
                    minWidth: 0,
                    height: { xs: '40px', lg: '48px' },
                    borderRadius: { xs: '24px', lg: '4px' },
                    borderColor: isRetry ? '#757A89' : undefined,
                    textTransform: 'none',
                    fontFamily: 'var(--font-bricolage-bold)',
                    fontSize: { xs: '14px', lg: '16px' },
                    lineHeight: { xs: '17px', lg: '19px' },
                  }}
                >
                  {isRetry
                    ? errorT('tryAgain')
                    : isNotFound
                      ? notFoundT('button')
                      : errorT('button')}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {showRecommendations ? recommendations : null}
    </main>
  );
}
